import { useState, FC, MouseEvent } from 'react';

import {
  Avatar,
  Tooltip,
  Popper,
  Paper,
  Box,
  Typography,
  Divider,
  TooltipProps,
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';

import { formatPersonName, getPersonInitials } from '@/utils';

interface Viewer {
  name: string;
  email: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

/**
 * Prefer the name parts, which can be ordered into the display format. Fall
 * back to the pre-composed `name` for viewers running a bundle deployed before
 * the presence payload carried parts.
 */
const viewerDisplayName = (viewer: Viewer) =>
  formatPersonName(viewer) || viewer.name;

interface TooltipContentProps {
  viewer: Viewer;
}

interface ActiveViewersProps {
  viewers: Viewer[];
}

const StyledAvatar = styled(Avatar)(({ theme }: { theme: Theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: '#CFD6DA',
  color: '#0E3145',
  fontSize: '0.875rem',
  fontWeight: 500,
  '&:hover': {
    border: `1px solid ${theme.palette.error.main}`,
  },
}));

const ViewerTooltip = styled(
  ({ className, ...props }: TooltipProps & { className?: string }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
)(({ theme }: { theme: Theme }) => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#0E3145',
    padding: theme.spacing(1.5),
    maxWidth: 'none',
  },
}));

const TooltipContent: FC<TooltipContentProps> = ({ viewer }) => (
  <Paper
    sx={{
      bgcolor: '#0E3145',
      borderRadius: '8px',
      width: 'fit-content',
    }}
  >
    <Box key={viewer.email}>
      <Box
        sx={{
          px: 1,
          py: 0.5,
        }}
      >
        <Typography
          sx={{
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '16px',
          }}
        >
          {viewerDisplayName(viewer)}
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '16px',
          }}
        >
          {viewer.email}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const ActiveViewers: FC<ActiveViewersProps> = ({
  viewers,
}: ActiveViewersProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const displayedViewers = viewers.slice(0, 3);
  const remainingViewers = viewers.length > 3 ? viewers.slice(3) : [];

  const handlePopoverOpen = (event: MouseEvent<HTMLDivElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  const RemainingViewersContent: FC = () => {
    return (
      <Paper
        sx={{
          bgcolor: '#0E3145',
          borderRadius: '8px',
          width: 'fit-content',
          marginTop: 1,
        }}
      >
        {remainingViewers.map((viewer, index) => (
          <Box key={viewer.email}>
            <Box
              sx={{
                px: 3,
                py: 1,
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '16px',
                }}
              >
                {viewerDisplayName(viewer)}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '16px',
                }}
              >
                {viewer.email}
              </Typography>
            </Box>
            {index !== remainingViewers.length - 1 && (
              <Divider
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                }}
              />
            )}
          </Box>
        ))}
      </Paper>
    );
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {displayedViewers.map((viewer) => (
        <ViewerTooltip
          key={viewer.email}
          title={<TooltipContent viewer={viewer} />}
          placement="bottom"
        >
          <StyledAvatar>
            {getPersonInitials(viewer) ||
              getPersonInitials({ name: viewer.name })}
          </StyledAvatar>
        </ViewerTooltip>
      ))}

      {remainingViewers.length > 0 && (
        <Box>
          <StyledAvatar
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            aria-owns={open ? 'mouse-over-popover' : undefined}
            aria-haspopup="true"
          >
            +{remainingViewers.length}
          </StyledAvatar>
          <Popper
            id="mouse-over-popover"
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            sx={{ zIndex: 1300 }}
          >
            <RemainingViewersContent />
          </Popper>
        </Box>
      )}
    </Box>
  );
};

export default ActiveViewers;
